// Условия «Матрицы прав» в адресе страницы (T160; README «Плотные таблицы»; ux.md, раздел 6):
// поиск по операции — по названию и коду — и скрытые в «Колонках» роли. Матрица одна на сервис
// и небольшая (GET /api/rights, без параметров), поэтому условия применяются к загруженной
// матрице в браузере. Колонку «Операция» скрыть нельзя: она закреплена.
import { roles } from '@kp/contracts';
import type { Operation, Role } from '@kp/contracts';
import { operationLabels } from '../../app/labels.ts';

export interface RightsSearch {
  /** Часть названия или кода операции, как её ввели: «журнал», «users.read». */
  q?: string;
  /** Скрытые колонки ролей через запятую в порядке ролей: «controller,observer». */
  hide?: string;
}

/** Длина поиска: название операции короче. */
export const RIGHTS_SEARCH_MAX = 64;

function isRole(value: string): value is Role {
  return (roles as readonly string[]).includes(value);
}

/** Скрытые роли: только известные, без повторов, в порядке ролей; пусто — undefined. */
function hideValue(hidden: readonly string[]): string | undefined {
  const known = roles.filter((role) => hidden.includes(role));
  return known.length > 0 ? known.join(',') : undefined;
}

/** Для адреса страницы: validateSearch маршрута /rights. */
export function parseRightsSearch(search: Record<string, unknown>): RightsSearch {
  const result: RightsSearch = {};
  if (typeof search.q === 'string' && search.q.trim() !== '') {
    result.q = search.q.slice(0, RIGHTS_SEARCH_MAX);
  }
  if (typeof search.hide === 'string') {
    const hide = hideValue(search.hide.split(',').map((item) => item.trim()));
    if (hide) result.hide = hide;
  }
  return result;
}

/** Скрытые в «Колонках» роли. */
export function hiddenRoles(search: RightsSearch): Role[] {
  return (search.hide ?? '').split(',').filter(isRole);
}

/** Те же условия с другим набором скрытых ролей. */
export function withHiddenRoles(search: RightsSearch, hidden: readonly Role[]): RightsSearch {
  const next: RightsSearch = { ...search };
  delete next.hide;
  const hide = hideValue(hidden);
  return hide ? { ...next, hide } : next;
}

/** Поиск сужает матрицу: «Ничего не найдено» вместо пустой таблицы. Колонки — не условие. */
export function hasRightsFilters(search: RightsSearch): boolean {
  return Boolean(search.q?.trim());
}

/**
 * Строка матрицы подходит под поиск: часть названия любой её операции или кода без учёта
 * регистра — «журнал», «Users.Read».
 */
export function matchesOperation(operations: readonly Operation[], q: string | undefined): boolean {
  const needle = q?.trim().toLocaleLowerCase('ru-RU') ?? '';
  if (needle === '') return true;
  return operations.some(
    (operation) =>
      operationLabels[operation].toLocaleLowerCase('ru-RU').includes(needle) ||
      operation.toLocaleLowerCase('ru-RU').includes(needle),
  );
}
